import {
  ButtonV3,
  GridItem,
  LayoutGrid,
  TypographyV3,
} from '@jouzen/ecom-components';
import { useGateValue } from '@statsig/react-bindings';
import { useTranslations } from 'next-intl';

import { EventType } from '@/analytics/types';
import LandingPageHeroChiclet from '@/app/components/LandingPage/LandingPageHero/LandingPageHeroChiclet';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';
import SectionHeading from '@/app/components/SectionHeading';

import HomeHeroWithChicletWrapper from '../HomeHeroWithChicletWrapper';
import GutfHeroBackground from './common/GiveUsTheFingerHeroBackground';
import GiveUsTheFingerVideoModal from './common/GiveUsTheFingerVideoModal';
import HsaFsaIcon from './common/HsaFsaIcon';

const GiveUsTheFingerHero = () => {
  const t = useTranslations();
  const gutfIncludeVideoChiclet = useGateValue('give_us_the_finger_chiclet');

  const onVideoModalOpen = () => {
    void window.ouraAnalytics.track(EventType.LinkClicked, {
      cta: 'Watch the Film',
      location: 'chiclet',
      action: 'play video',
    });
  };

  return (
    <HomeHeroWithChicletWrapper data-cy="gutf-hero-wrapper">
      <section
        className="lg:pb-4' h-[calc(100svh-70px)] bg-sandstone-200 md:h-[calc(100svh-49px)]"
        data-cy="gutf-hero"
      >
        <LayoutGrid className="h-full">
          <GridItem
            className="h-full overflow-hidden max-lg:bg-black! lg:mx-5 lg:rounded-b-xl"
            colEnd={{ sm: 'full' }}
            colStart={{ sm: 'full' }}
            rowEnd={{ sm: 2 }}
            rowStart={{ sm: 1 }}
          >
            <GutfHeroBackground />
          </GridItem>
          <GridItem
            className="z-20 clip-polygon md:relative lg:mx-5"
            colEnd={{ sm: 'full' }}
            colStart={{ sm: 'full' }}
            rowEnd={{ sm: 2 }}
            rowStart={{ sm: 1 }}
          >
            <div
              className="absolute top-0 left-0 h-svh w-full lg:-top-12 lg:bottom-0 lg:h-auto"
              data-cy="gutf-hero-overlay"
            >
              <LayoutGrid
                className="h-full content-end! lg:px-15 lg:pb-4"
                rowGap={0}
              >
                <GridItem
                  colEnd={{ sm: 'main' }}
                  colStart={{ sm: 'main', md: 'main', lg: 1 }}
                >
                  <Motion
                    initial={{
                      opacity: 0,
                      translateY: 25,
                      filter: 'blur(5px)',
                    }}
                    animate={{ opacity: 1, translateY: 0, filter: 'blur(0)' }}
                    transition={{ duration: 1.25, type: 'tween', delay: 1 }}
                  >
                    <div className="mt-24 mb-5 lg:mb-22">
                      <SectionHeading
                        content="gutf_hero_title"
                        emProps={{
                          className: 'block pt-2 lg:pt-4',
                        }}
                        typographyProps={{
                          className:
                            'leading-tighter text-center text-5xl md:text-6xl lg:text-7xl xl:text-8xl xxl:text-9xl lg:text-left', // TODO: design has 120px as largest, but our sizes only go up to 96px
                          font: 'serif',
                          weight: 'light',
                          color: 'light',
                          Element: 'h1',
                          variant: 'h1',
                        }}
                      />
                    </div>
                  </Motion>
                </GridItem>
                <GridItem
                  className="flex flex-col items-center self-end lg:block"
                  colEnd={{ sm: 'main', md: 20, lg: 10, xl: 8 }}
                  colStart={{ sm: 'main', md: 6, lg: 1, xl: 1 }}
                >
                  <Motion
                    className="w-full lg:w-auto"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 2, type: 'tween', delay: 1.25 }}
                  >
                    <Motion
                      animate={{ opacity: 1 }}
                      data-cy="gutf-hero-content"
                      initial={{ opacity: 0 }}
                      transition={{ duration: 1, type: 'tween' }}
                    >
                      <TypographyV3
                        align="centerLgLeft"
                        className="mb-4 text-xl lg:mb-6"
                        color="light"
                        data-cy="home-hero-content-copy"
                        Element="p"
                        variant="body-large"
                      >
                        <RichText messageKey="gutf_hero_copy" />
                      </TypographyV3>
                      <div className="mb-5 flex w-full flex-col items-center space-y-3 lg:mb-12 lg:items-start">
                        <div className="mb-4 flex justify-center lg:hidden">
                          <HsaFsaIcon />
                        </div>
                        <ButtonV3
                          href="/product/rings"
                          className="mb-18 font-bold lg:mb-4 lg:w-auto"
                          data-cy="gutf-hero-content-button"
                          variant="primary"
                          onClick={() => {
                            void window.ouraAnalytics.track(
                              EventType.CTAClicked,
                              {
                                cta: 'explore oura ring 4',
                                action: 'go to pop',
                                location: 'hero',
                                path: '/product/rings',
                              },
                            );
                          }}
                        >
                          {t('explore_oura_ring_4')}
                        </ButtonV3>
                        <div className="hidden justify-center lg:flex">
                          <HsaFsaIcon />
                        </div>
                      </div>
                    </Motion>
                  </Motion>
                </GridItem>
              </LayoutGrid>
            </div>
          </GridItem>
        </LayoutGrid>
      </section>
      {gutfIncludeVideoChiclet && (
        <>
          <div
            className={
              'right-6 bottom-8 z-30 mt-6 flex justify-center px-5 lg:absolute lg:right-14 lg:bottom-11 lg:mt-0'
            }
            data-cy="gutf-hero-chiclet-wrapper"
          >
            <LandingPageHeroChiclet
              className="mb-5! lg:mb-0"
              title="gutf_hero_chiclet_copy"
              modal={{
                cta: {
                  className: 'w-fit px-0',
                  label: 'watch_the_film',
                  onClick: onVideoModalOpen,
                },
                Dialog: GiveUsTheFingerVideoModal,
              }}
              image={{
                format: 'jpg',
                localizedAlt: 'gutf_chiclet_img_alt',
                src: '/gutf-campaign/gutf-chiclet-2.jpg',
              }}
            />
          </div>
        </>
      )}
    </HomeHeroWithChicletWrapper>
  );
};

export default GiveUsTheFingerHero;
